import { useEffect, useState } from "react";
import { FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import { apiFetch } from "./api";

const EDITABLE_FIELDS = [
  { key: "birthdate", label: "Birthdate", type: "date" },
  { key: "graduation_year", label: "Graduation Year", type: "number", placeholder: "e.g. 2027" },
  { key: "high_school", label: "High School", type: "select" },
  { key: "height", label: "Height", type: "text", placeholder: "e.g. 6 ft 2 in" },
  { key: "weight", label: "Weight", type: "text", placeholder: "e.g. 185 lbs" },
  {
    key: "two_k_pr",
    label: "2K PR",
    type: "text",
    placeholder: "hh:mm:ss, e.g. 00:07:15",
    pattern: /^[0-9]{2}:[0-5][0-9]:[0-5][0-9]$/,
    patternError: "Enter your 2K PR as hh:mm:ss, e.g. 00:07:15",
  },
];

const READ_ONLY_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "age_group", label: "Age Group" },
  { key: "team", label: "Team" },
  { key: "squad", label: "Squad" },
];

function displayValue(field, value) {
  if (value == null || value === "") return "—";
  if (field.type === "date") return String(value).slice(0, 10);
  return value;
}

export default function ProfileTab({ user }) {
  const [profile, setProfile] = useState(null);
  const [highSchools, setHighSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [savingField, setSavingField] = useState(null);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError("");
      try {
        const [profileData, schools] = await Promise.all([
          apiFetch("/users/me/profile"),
          apiFetch("/high-schools"),
        ]);
        if (cancelled) return;
        setProfile(profileData);
        setHighSchools(Array.isArray(schools) ? schools : []);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function startEdit(field) {
    const raw = profile?.[field.key];
    setEditingField(field.key);
    setFieldError("");
    setDraftValue(field.type === "date" && raw ? String(raw).slice(0, 10) : raw ?? "");
  }

  function cancelEdit() {
    setEditingField(null);
    setDraftValue("");
    setFieldError("");
  }

  async function saveEdit(field) {
    const trimmed = typeof draftValue === "string" ? draftValue.trim() : draftValue;

    if (field.pattern && trimmed !== "" && !field.pattern.test(trimmed)) {
      setFieldError(field.patternError || "Invalid format");
      return;
    }

    setSavingField(field.key);
    setFieldError("");
    try {
      const payload = { [field.key]: trimmed === "" ? null : trimmed };
      const result = await apiFetch("/users/me/profile", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (result?.user) {
        setProfile(result.user);
      }
      setEditingField(null);
      setDraftValue("");
    } catch (e) {
      setFieldError(e?.message || "Failed to update field");
    } finally {
      setSavingField(null);
    }
  }

  if (loading) {
    return (
      <div className="card">
        <h2 className="h2">Profile</h2>
        <p className="help">Loading...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="h2">Profile</h2>

      {loadError && <p className="help" style={{ color: "#dc2626" }}>{loadError}</p>}

      <div className="profile-field-grid">
        {READ_ONLY_FIELDS.map((field) => (
          <div key={field.key} className="profile-field-row">
            <span className="profile-field-label">{field.label}</span>
            <span className="profile-field-value">
              {displayValue(field, profile?.[field.key] ?? user?.[field.key])}
            </span>
          </div>
        ))}

        {EDITABLE_FIELDS.map((field) => {
          const isEditing = editingField === field.key;
          const isSaving = savingField === field.key;
          return (
            <div key={field.key} className="profile-field-row">
              <span className="profile-field-label">{field.label}</span>
              {isEditing ? (
                <div className="profile-field-edit">
                  {field.type === "select" ? (
                    <select
                      className="input"
                      autoFocus
                      value={draftValue || ""}
                      onChange={(e) => setDraftValue(e.target.value)}
                    >
                      <option value="">Select...</option>
                      {highSchools.map((hs) => (
                        <option key={hs.id} value={hs.name}>
                          {hs.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input"
                      autoFocus
                      type={field.type}
                      placeholder={field.placeholder}
                      value={draftValue || ""}
                      onChange={(e) => setDraftValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(field);
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                  )}
                  <button
                    type="button"
                    className="btn ghost profile-field-btn"
                    aria-label={`Save ${field.label}`}
                    onClick={() => saveEdit(field)}
                    disabled={isSaving}
                  >
                    <FaCheck />
                  </button>
                  <button
                    type="button"
                    className="btn ghost profile-field-btn"
                    aria-label={`Cancel editing ${field.label}`}
                    onClick={cancelEdit}
                    disabled={isSaving}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div className="profile-field-view">
                  <span className="profile-field-value">{displayValue(field, profile?.[field.key])}</span>
                  <button
                    type="button"
                    className="btn ghost profile-field-btn"
                    aria-label={`Edit ${field.label}`}
                    onClick={() => startEdit(field)}
                  >
                    <FaEdit />
                  </button>
                </div>
              )}
              {isEditing && fieldError && (
                <p className="help profile-field-error">{fieldError}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
